import { useState } from 'react';
import './App.css';
import { RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const SEVERITY_COLORS = {
  Critical: 'bg-red-100 text-red-800',
  High: 'bg-orange-100 text-orange-800',
  Medium: 'bg-yellow-100 text-yellow-800',
  Low: 'bg-green-100 text-green-800',
};

function AlertInputCell({ rawLog }) {
  const [expanded, setExpanded] = useState(false);
  if (!rawLog || rawLog === 'N/A') return <span className="text-gray-400 text-xs">—</span>;

  const lines = rawLog.split('\n').filter(Boolean);
  const preview = lines.slice(0, 2);
  const rest = lines.slice(2);

  return (
    <div className="text-xs font-mono text-gray-700 space-y-1">
      {preview.map((line, i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded px-2 py-1 break-all">{line}</div>
      ))}
      {expanded && rest.map((line, i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded px-2 py-1 break-all">{line}</div>
      ))}
      {rest.length > 0 && (
        <button onClick={() => setExpanded(!expanded)} className="text-blue-600 underline">
          {expanded ? 'Show less' : `+${rest.length} more lines`}
        </button>
      )}
    </div>
  );
}

function AnalysisCell({ analysis }) {
  if (!analysis) return <span className="text-gray-400 text-sm">N/A</span>;

  const severity = analysis.severity || 'Medium';
  const badgeClass = SEVERITY_COLORS[severity] || 'bg-gray-100 text-gray-700';

  return (
    <div className="space-y-1 text-sm">
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${badgeClass}`}>
        {severity}
      </span>
      <p className="text-gray-700"><span className="font-medium">Root Cause:</span> {analysis.root_cause}</p>
      <p className="text-gray-700"><span className="font-medium">Analysis:</span> {analysis.analysis}</p>
      {analysis.remediation?.length > 0 && (
        <ul className="list-disc list-inside text-gray-600 text-xs mt-1">
          {analysis.remediation.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
      <p className="text-xs text-gray-500">
        Confidence: {analysis.confidence} ({analysis.confidence_score}/10) &nbsp;|&nbsp;
        FP: {analysis.false_positive_probability}
      </p>
    </div>
  );
}

function MailCell({ mailSent, onSend }) {
  if (mailSent) {
    return (
      <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-1 rounded text-xs font-medium">
        ✅ Mail Sent
      </span>
    );
  }
  return (
    <button
      onClick={onSend}
      className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded transition-colors"
    >
      Send Mail
    </button>
  );
}

export default function App() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [mailStatus, setMailStatus] = useState({});
  const [generating, setGenerating] = useState(false);

  const fetchData = () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000);

    fetch(`${API_URL}/graylog/correlated`, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.status === 'no_data') {
          setResults([]);
          setSummary(null);
          return;
        }
        setSummary(data.summary || null);
        const tier1 = data.tier1_pattern_analysis || [];
        const tier2 = data.tier2_noise_report;
        const tier2Row = tier2 ? [{
          alert_id: 'batch-noise',
          raw_log: tier2.summary || 'Batch of noise logs',
          input: { source_ip: 'Multiple (batch)' },
          analysis: {
            root_cause: tier2.summary || 'Batch noise report',
            severity: tier2.overall_risk || 'Low',
            analysis: (tier2.patterns_detected || []).map(p => p.description).join(' | ') || tier2.summary,
            remediation: tier2.recommended_actions || [],
            false_positive_probability: tier2.false_positive_estimate || 'Medium',
            confidence: 'Medium',
            confidence_score: 5
          }
        }] : [];
        setResults([...tier1, ...tier2Row]);
      })
      .catch(err => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });
  };

  const generateLogs = () => {
    setGenerating(true);
    fetch(`${API_URL}/generate-logs`, { method: 'POST' })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') {
          alert(`✅ ${data.total_sent} logs sent to Graylog (${data.normal_logs} normal + ${data.attack_logs} attack). Click refresh to analyse.`);
        } else {
          alert(`❌ Failed: ${data.detail || data.message}`);
        }
      })
      .catch(err => alert(`Error: ${err.message}`))
      .finally(() => setGenerating(false));
  };

  const handleSendMail = (row) => {
    const alertId = row.alert_id;
    const sourceIp = row.input?.source_ip || row.input?.src_ip || '—';

    fetch(`${API_URL}/send-mail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        alert_id: alertId,
        source_ip: sourceIp,
        raw_log: row.raw_log || '—',
        analysis: row.analysis || {}
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'sent') {
          setMailStatus(prev => ({ ...prev, [alertId]: true }));
        } else {
          alert(`Mail failed: ${data.detail || data.message}`);
        }
      })
      .catch(err => alert(`Mail error: ${err.message}`));
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold text-gray-800">AI Log Analyser</h1>
          <button
            onClick={fetchData}
            disabled={loading}
            title="Refresh"
            className="p-2 rounded-full hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RefreshCw
              className={`w-6 h-6 text-gray-600 ${loading ? 'animate-spin' : ''}`}
            />
          </button>
          <button
            onClick={generateLogs}
            disabled={generating}
            title="Generate & send logs to Graylog"
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs px-3 py-1.5 rounded transition-colors"
          >
            {generating ? 'Sending...' : '⚡ Generate Logs'}
          </button>
        </div>

        {summary && (
          <div className="flex items-center gap-3 text-sm">
            <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm text-center">
              <p className="text-2xl font-bold text-gray-800">{summary.total_raw_logs}</p>
              <p className="text-xs text-gray-500 mt-0.5">Logs Fetched</p>
            </div>
            <div className="bg-white border border-red-200 rounded-lg px-4 py-2 shadow-sm text-center">
              <p className="text-2xl font-bold text-red-600">{summary.anomalous_log_count}</p>
              <p className="text-xs text-gray-500 mt-0.5">Anomalous Logs</p>
            </div>
            <div className="bg-white border border-green-200 rounded-lg px-4 py-2 shadow-sm text-center">
              <p className="text-2xl font-bold text-green-600">{summary.noise_alerts}</p>
              <p className="text-xs text-gray-500 mt-0.5">Uncorrelated Logs</p>
            </div>
          </div>
        )}
      </div>

      {loading && results.length === 0 && (
        <div className="text-center text-gray-500 py-20 text-lg">Analysing logs...</div>
      )}

      {loading && results.length > 0 && (
        <div className="text-sm text-blue-600 mb-3 animate-pulse">Refreshing...</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-4 mb-4">
          Failed to fetch results: {error}
        </div>
      )}

      {!loading && !error && results.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m14 0v-6a2 2 0 00-2-2h-2a2 2 0 00-2 2v6m-7 0h10" />
          </svg>
          <p className="text-lg font-medium">No logs found in Graylog</p>
          <p className="text-sm mt-1">Click the refresh button once logs are available.</p>
        </div>
      )}

      {!error && results.length > 0 && (
        <div className="overflow-x-auto rounded-xl shadow">
          <table className="min-w-full bg-white text-sm">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left w-32">Source IP</th>
                <th className="px-4 py-3 text-left w-72">Alert Input</th>
                <th className="px-4 py-3 text-left">LLM Analysis</th>
                <th className="px-4 py-3 text-left w-28">Mail Status</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row, idx) => {
                const alertId = row.alert_id || idx;
                const sourceIp = row.input?.source_ip || row.input?.src_ip || '—';
                return (
                  <tr key={alertId} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-4 font-mono text-gray-700 align-top">
                      <div>{sourceIp}</div>
                      {row.correlated_count && (
                        <span className="text-xs text-gray-400 mt-1 block">{row.correlated_count} logs</span>
                      )}
                      {alertId === 'batch-noise' && summary && (
                        <span className="text-xs text-gray-400 mt-1 block">{summary.noise_alerts} logs</span>
                      )}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <AlertInputCell rawLog={row.raw_log} />
                    </td>
                    <td className="px-4 py-4 align-top">
                      <AnalysisCell analysis={row.analysis} />
                    </td>
                    <td className="px-4 py-4 align-top">
                      <MailCell
                        mailSent={!!mailStatus[alertId]}
                        onSend={() => handleSendMail(row)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
